import useConnectWallet from "@/store/wallet/useConnectWallet";
import { useState } from "react";
import { toast } from "react-toastify";

/**
 * useParticipate - 参与抽奖 Hook（交易类）
 * 
 * 用于调用 Lottery 合约的 participate 方法参与抽奖。
 * 需要发送指定数量的主网币作为参与费用。
 * 通过 tx.wait() 等待交易确认，使用 toast 显示交易结果提示。
 * 
 * @returns {Object} 返回参与抽奖的方法和加载状态
 *   - participate: 执行参与抽奖的函数
 *   - participateLod: 加载状态，表示交易是否正在进行中
 */
const useParticipate = () => {
    // 获取合约
    const {
        contractEvm: { Lottery },
    } = useConnectWallet();
    // loading
    const [participateLod, setLoading] = useState(false);

    /**
     * 参与抽奖函数
     * @param amount - 参与抽奖需要的金额（bigint）
     * @param toastText - toast 提示文本
     * @param suuFun - 成功回调函数（交易上链后）
     * @param errFun - 失败回调函数
     * @param onTransactionSent - 交易发送后立即触发的回调（用户确认后）
     */
    const participate = async (
        amount: bigint,
        toastText: { success: string; error: string },
        suuFun?: () => void,
        errFun?: () => void,
        onTransactionSent?: () => void
    ) => {
        if (Lottery) {
            setLoading(true);
            try {
                // 调用合约的 participate 方法，发送指定数量的主网币
                const tx = await Lottery.participate({
                    value: amount
                });

                // 交易发送成功（用户已确认），立即触发回调
                onTransactionSent && onTransactionSent();

                // 等待交易确认
                await tx.wait();
                // 显示成功提示
                toastText.success && toast.success(toastText.success);
                // 成功后调用成功回调函数
                suuFun && suuFun();
            } catch (e) {
                console.log("useParticipate", e);
                // 失败后调用失败回调函数
                errFun && errFun();
                // 显示失败提示
                toast.error(toastText.error);
            } finally {
                setLoading(false);
            }
        }
    };

    return { participate, participateLod };
};

export default useParticipate;
